import { createFeatureSelector, createSelector } from '@ngrx/store';
import _ from 'lodash-es';
import { selectAllComparables } from '../../../comparables.selectors';
import { StudyDataState } from '../../../reducers';
import { selectLoadingFlag } from '../company-drill-down/drill-down.selectors';

export const selectStudyDataState = createFeatureSelector<StudyDataState>('selectedStudy');

export const selectSlideOutData = createSelector(selectStudyDataState, (data) => data.slideOut);

export const selectCompanySearchData = createSelector(selectSlideOutData, (slideOut) => slideOut.companySearch);

export const selectSearch = createSelector(selectCompanySearchData, (data) => data.search);

export const selectItemsPerPage = createSelector(selectCompanySearchData, (data) => data.itemsPerPage);

export const selectCurrentSearchHash = createSelector(selectCompanySearchData, (data) => data.currentSearchHash);

export const selectCurrentSearchInput = createSelector(selectCompanySearchData, (data) => data.currentSearchInput);

export const selectCurrentPage = createSelector(selectCompanySearchData, (data) => data.currentPage);

export const selectNextPage = createSelector(selectCompanySearchData, (data) => data.nextPage);

export const selectNextPageData = createSelector(selectSearch, selectCurrentSearchHash, selectNextPage, (search, hash, page) =>
  !!search && !!search[hash] && !!search[hash].pages && !!search[hash].pages[page] ? search[hash].pages[page] : undefined
);

export const isNextPageSuccess = createSelector(selectNextPageData, (pageData) => (!pageData ? false : pageData.isSuccess));

export const isNextPageFailed = createSelector(selectNextPageData, (pageData) => (!pageData ? false : pageData.isFailed));

export const selectTotalCount = createSelector(selectSearch, selectCurrentSearchHash, (search, hash) =>
  !!search && !!search[hash] && !!search[hash].totalCount ? search[hash].totalCount : 0
);

export const selectCompanyIds = createSelector(selectSearch, selectCurrentSearchHash, selectCurrentPage, (search, hash, page) =>
  !!search && !!search[hash] && !!search[hash].pages && !!search[hash].pages[page] ? search[hash].pages[page].companyIds : []
);

export const selectCompanies = createSelector(selectAllComparables, selectCompanyIds, (comparables, ids) =>
  ids.map((id) => {
    const comparable = comparables[id];
    const databaseId = comparable.databaseId;

    return {
      comparableId: id,
      databaseId: databaseId,
      companyName: comparable.companyName,
      countryName: comparable.companyInfo[databaseId].countryName,
      bvdId: comparable.companyInfo[databaseId].idNumber,
      databases: comparable.databases,
    };
  })
);

export const isNextPageLoading = createSelector(selectNextPageData, (pageData) => (!pageData ? false : pageData.isFetching));

export const isLoading = createSelector(isNextPageLoading, selectLoadingFlag, (loadingPage, loadingDetails) => loadingPage || loadingDetails);

export const isNextPageLoaded = createSelector(selectCurrentPage, selectNextPage, (currentPage, nextPage) => nextPage === currentPage);

export const selectSelectedCompaniesCount = createSelector(selectCompanySearchData, (data) =>
  !data.selectedCompanies ? 0 : _.flatten(Object.values(data.selectedCompanies)).length
);

export const selectSelectedCompaniesMap = createSelector(selectCompanySearchData, (data) => data.selectedCompanies || {});

export const selectSelectedCompanyIds = createSelector(selectCompanySearchData, (data) =>
  !data.selectedCompanies ? [] : _.flatten(Object.values(data.selectedCompanies)).map((c) => c.comparableId)
);

export const selectSelectedCompanies = createSelector(selectAllComparables, selectSelectedCompanyIds, (comparables, ids) =>
  ids.map((id) => {
    return { companyName: comparables[id].companyName, comparableId: id };
  })
);

export const selectSearchData = createSelector(
  selectTotalCount,
  selectItemsPerPage,
  selectCurrentPage,
  selectCurrentSearchHash,
  selectCurrentSearchInput,
  isNextPageSuccess,
  isNextPageFailed,
  (totalCount, itemsPerPage, currentPage, currentSearchHash, currentSearchInput, isNextPageSuccess, isNextPageFailed) => {
    return {
      totalCount,
      itemsPerPage,
      currentPage,
      currentSearchHash,
      currentSearchInput,
      isNextPageSuccess,
      isNextPageFailed,
    };
  }
);

export const selectComparableIdsByPageNumber = createSelector(selectSearch, selectCurrentSearchHash, (search, hash) =>
  !!search && !!search[hash] && !!search[hash].pages
    ? _.mapValues(search[hash].pages, function (data) {
        return data.companyIds;
      })
    : undefined
);
